import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useInView } from 'react-intersection-observer'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChatMessage } from './ChatMessage'
import { ChatSpinnerContainer, VirtualizedMessageListContainer } from './style'
import { CircularProgress } from '@material-ui/core'
import { ScrollToBottom } from './buttons/ScrollToBottom'

export const VirtualizedMessageList = ({
  messages,
  user,
  isDesktop,
  getModeratorColorByUsername,
  hasMoreMessages,
  fetchMoreMessages,
  isFetchingMoreMessages,
  pinnedMessageRef,
}) => {
  const [lastItemRef, inView] = useInView()
  const [autoScroll, setAutoScroll] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [paginationOffSet, setPaginationOffSet] = useState(0)
  const parentRef = useRef(null)
  const lastScrollTop = useRef(0)
  const lastMessagesLengthRef = useRef(messages.length)
  const lastHeight = useRef(0)

  const rowVirtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (isDesktop ? 55 : 35),
    onChange: (virtualizer) => {
      const offsetForLastIndex = virtualizer.getOffsetForIndex(messages.length - 1)
      if (offsetForLastIndex !== undefined) {
        if (virtualizer.scrollOffset >= offsetForLastIndex[0] && autoScroll === false) {
          setAutoScroll(true)
          return
        }
      }
    },
  })

  const scrollToBottom = () => {
    rowVirtualizer.scrollToOffset(rowVirtualizer.getTotalSize(), {
      behavior: 'auto',
    })
  }

  useEffect(() => {
    lastMessagesLengthRef.current = messages.length

    if (autoScroll === false || messages.length === 0) {
      return
    }

    scrollToBottom()
  }, [messages.length, pinnedMessageRef.current])

  useEffect(() => {
    if (inView === true && hasMoreMessages === true && !isInitialLoad) {
      fetchMoreMessages()
    }
  }, [inView, hasMoreMessages])

  useEffect(() => {
    if (messages.length > 0 && isInitialLoad) {
      setTimeout(() => {
        scrollToBottom()
        setIsInitialLoad(false)
      }, 500)
    }
  }, [messages])

  useEffect(() => {
    const parentElement = parentRef.current
    if (!parentElement) return

    const handleScroll = () => {
      const scrollTop = parentElement.scrollTop
      const height = parentElement.clientHeight

      const messagesLengthChanged = lastMessagesLengthRef.current !== messages.length

      if (
        scrollTop < lastScrollTop.current && // New scroll top is smaller than old scroll top (scrolling up)
        !messagesLengthChanged && // Messages length did not change
        height === lastHeight.current // Height did not change
      ) {
        setAutoScroll(false)
      }

      lastScrollTop.current = scrollTop
      lastHeight.current = height
      lastMessagesLengthRef.current = messages.length
    }

    parentElement.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      parentElement?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (isFetchingMoreMessages) {
      setPaginationOffSet(rowVirtualizer.getTotalSize())
    } else {
      if (isInitialLoad) return
      rowVirtualizer.scrollToOffset(rowVirtualizer.getTotalSize() - paginationOffSet, {
        behavior: 'auto',
      })
      setPaginationOffSet(rowVirtualizer.getTotalSize())
    }
  }, [isFetchingMoreMessages])

  return (
    <>
      {isFetchingMoreMessages && isDesktop ? (
        <ChatSpinnerContainer loading isDesktop>
          <CircularProgress size="1.5rem" color="primary" />
        </ChatSpinnerContainer>
      ) : null}

      <VirtualizedMessageListContainer ref={parentRef} isDesktop={isDesktop}>
        <div
          className="relative"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => (
            <div
              key={virtualItem.key}
              ref={rowVirtualizer.measureElement}
              data-index={virtualItem.index}
              className="absolute left-0 top-0 w-full"
              style={{
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {virtualItem.index === 0 ? <div ref={lastItemRef} data-for="last-item"></div> : null}
              {messages[virtualItem.index] !== undefined ? (
                <ChatMessage
                  isDesktop={isDesktop}
                  user={user}
                  getModeratorColorByUsername={getModeratorColorByUsername}
                  message={messages[virtualItem.index]}
                />
              ) : null}
            </div>
          ))}
        </div>
      </VirtualizedMessageListContainer>
      <div
        className="absolute right-0"
        style={{
          bottom: isDesktop ? '80px' : 'auto',
          right: '0',
        }}
      >
        <ScrollToBottom onClick={scrollToBottom} isMessageListScrolledToBottom={autoScroll} />
      </div>
    </>
  )
}

VirtualizedMessageList.propTypes = {
  messages: PropTypes.array,
  parentRef: PropTypes.object,
  isDesktop: PropTypes.bool,
  hasMoreMessages: PropTypes.bool,
  fetchMoreMessages: PropTypes.func,
  isFetchingMoreMessages: PropTypes.bool,
  user: PropTypes.object,
  getModeratorColorByUsername: PropTypes.func,
  pinnedMessageRef: PropTypes.object,
}
